import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { retrySupabase } from '../utils/retry';

// Define the shape of our permissions object
export interface UserPermissions {
  'registro-ventas': boolean;
  'historial-ventas': boolean;
  'mensajes': boolean;
  'clientes': boolean;
  'graficos-de-interaccion': boolean;
  'envios': boolean;
  'productos': boolean;
  'stock': boolean;
  'descuentos': boolean;
  'registro-compras': boolean;
  'historial-compras': boolean;
  'forecasting': boolean;
  'proveedores': boolean;
  'flujo-caja': boolean;
  'forecasting-financiero': boolean;
  'gastos-recurrentes': boolean;
  'estudio': boolean;
  'plantillas': boolean;
  'calendario': boolean;
  'imagenes': boolean;
  'crear-anuncio': boolean;
  'test-v1': boolean;
  'editor-video': boolean;
  'interfaz-v2': boolean;
  'em-mapa': boolean;
  'em-plan': boolean;
  // `em-insights` cubre "Análisis de datos" entero (Insights + Estrategia de
  // contenido, las dos pestañas). La clave `em-estrategia` se borró al
  // fusionarlas: quedó sin lector (ADR-2026-08-17-analisis-datos-tabs-por-ruta).
  'em-insights': boolean;
  'em-marca': boolean;
  /** "Fuentes de datos": conexiones + base de conocimiento. */
  'em-datos': boolean;
  // Chat con el agente de Atlas (`/chat` + panel lateral).
  'chat-atlas': boolean;
  [key: string]: boolean; // Allow for additional permissions
}

// Default permissions (all false)
const defaultPermissions: UserPermissions = {
  'registro-ventas': false,
  'historial-ventas': false,
  'mensajes': false,
  'clientes': false,
  'graficos-de-interaccion': false,
  'envios': false,
  'productos': false,
  'stock': false,
  'descuentos': false,
  'registro-compras': false,
  'historial-compras': false,
  'forecasting': false,
  'proveedores': false,
  'flujo-caja': false,
  'forecasting-financiero': false,
  'gastos-recurrentes': false,
  'estudio': true,
  'plantillas': false,
  'calendario': true,
  'imagenes': true,
  'crear-anuncio': true,
  'test-v1': true,
  'editor-video': true,
  'interfaz-v2': true,
  // Estudio de marketing — Bloque A. `true` porque el filtro del Sidebar
  // corta por permiso antes de mirar `devOnly`/`isDevTester`; el candado
  // real de "en pruebas" lo da `devOnly: true` en FLAT_MENU_ITEMS.
  'em-mapa': true,
  'em-plan': true,
  'em-insights': true,
  'em-marca': true,
  'em-datos': true,
  // Puesta en marcha (flujo de configuración inicial). `true` por lo mismo:
  // el ítem de la sidebar es andamiaje `devOnly` para poder entrar a probarlo,
  // y con el permiso en `false` no lo vería ni un dev tester.
  'em-onboarding': true,
  // Chat con el agente de Atlas. `true` por el MISMO motivo que las de arriba:
  // el filtro del Sidebar corta por permiso ANTES de mirar `devOnly` /
  // `isDevTester`, así que con `false` el ítem no aparecería ni siquiera para
  // los dev testers. El candado real de "en pruebas" lo da `devOnly: true` en
  // FLAT_MENU_ITEMS.
  'chat-atlas': true,
};

interface PermissionContextType {
  permissions: UserPermissions;
  loading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<UserPermissions>(defaultPermissions);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Get user's company schema
      const { data: profile } = await retrySupabase(async () =>
        await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single()
      );

      if (!profile?.id_company) {
        setLoading(false);
        return;
      }

      // config_users table no longer exists; default permissions apply
      const formattedPermissions: UserPermissions = { ...defaultPermissions };
      // Map "crear-anuncio" and "test-v1" to follow "estudio" as before
      formattedPermissions['crear-anuncio'] = formattedPermissions['estudio'];
      formattedPermissions['test-v1'] = formattedPermissions['estudio'];
      setPermissions(formattedPermissions);
    } catch (err) {
      console.error('Error in permission system:', err);
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  // Fetch permissions on mount
  useEffect(() => {
    fetchUserPermissions();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchUserPermissions();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    permissions,
    loading,
    error,
    refreshPermissions: fetchUserPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
